import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import TableSectionContext from '../table/table-section-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { TableSectionContextProps } from '../table/table.types';
import type { TableHeadProps } from './table-head.types';

import './table-head.scss';

/**
 * The component renders a `<thead>` element and it should contain `TableRow` components.
 *
 * ### Usage
 *
 * ```tsx
 * import { TableHead, TableRow, TableCell } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomHead() {
 *   return (
 *     <TableHead className="custom-head">
 *       <TableRow>
 *         <TableCell>This cell will be rendered as `<th>`</TableCell>
 *       </TableRow>
 *    </TableHead>
 *   );
 * }
 * ```
 */
const TableHead = forwardRef(
  (props: TableHeadProps, forwardedRef: ForwardedRef<HTMLTableSectionElement> | null): ReactElement => {
    const {
      children,
      className,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const contextValue = useMemo<TableSectionContextProps>(() => ({ variant: 'head' }), []);

    return (
      <TableSectionContext.Provider value={contextValue}>
        <thead className={cn('rbui-table-head', className)} ref={forwardedRef} {...rest}>
          {children}
        </thead>
      </TableSectionContext.Provider>
    );
  },
);

TableHead.displayName = 'TableHead';

export default TableHead;
